import * as React from "react";
import { useT } from "@/lib/i18n/use-t";
import { Label } from "../label";
import { Switch } from "../switch";
import { T } from "./csv-data-parser.i18n";
import { CsvFileParser } from "./csv-file-parser";
import { CsvTextParser } from "./csv-text-parser";
import { type CsvDataPoint, dataPointsToCsv } from "./csv-utils";

type InputMode = "text" | "file";

/**
 * CsvDataParser - A combined component for parsing CSV time series data.
 *
 * @description
 * Provides both textarea input and file upload options for CSV data.
 * Users can switch between modes using a toggle switch.
 * Returns parsed data via callback when validation succeeds.
 *
 * @example
 * ```tsx
 * import { CsvDataParser, CsvDataPoint } from "@/components/ui/csv-parser";
 *
 * const [data, setData] = useState<CsvDataPoint[] | null>(null);
 *
 * <CsvDataParser
 *   id="time-series"
 *   label="Historical Data"
 *   initialValue=""
 *   onParsedData={(data) => setData(data)}
 *   required
 * />
 * ```
 */
type CsvDataParserProps = {
  /** HTML id attribute for the input elements */
  id?: string;
  /** Label text displayed above the input */
  label?: string;
  /** Initial CSV text value for text mode */
  initialValue?: string;
  /** Callback fired when valid data is parsed (from either mode) */
  onParsedData: (data: CsvDataPoint[]) => void;
  /** Placeholder text for textarea */
  placeholder?: string;
  /** Number of visible text rows (default: 5) */
  rows?: number;
  /** Shows a required indicator (*) next to the label */
  required?: boolean;
  /** Custom error message for text parser */
  textErrorMessage?: string;
  /** Custom error message for file parser */
  fileErrorMessage?: string;
  /** Helper text shown under the text-area input */
  textHelpText?: string;
  /** Helper text shown under the file upload button */
  fileHelpText?: string;
  /** Label for text mode toggle (default: "Text Input") */
  textModeLabel?: string;
  /** Label for file mode toggle (default: "File Upload") */
  fileModeLabel?: string;
  /** Button text for file upload (default: "Upload CSV") */
  uploadButtonText?: string;
};

function CsvDataParser({
  id,
  label,
  initialValue = "",
  onParsedData,
  placeholder,
  rows = 5,
  required,
  textErrorMessage,
  fileErrorMessage,
  textHelpText,
  fileHelpText,
  textModeLabel,
  fileModeLabel,
  uploadButtonText,
}: CsvDataParserProps) {
  const t = useT(T);
  const [mode, setMode] = React.useState<InputMode>("text");
  const [textValue, setTextValue] = React.useState(initialValue);

  React.useEffect(() => {
    setTextValue(initialValue);
  }, [initialValue]);

  const handleModeChange = (checked: boolean) => {
    setMode(checked ? "file" : "text");
  };

  const handleTextValidation = (isValid: boolean, data: CsvDataPoint[] | null) => {
    if (isValid && data) {
      onParsedData(data);
    }
  };

  const handleFileParsed = (data: CsvDataPoint[]) => {
    const csvText = dataPointsToCsv(data);
    setTextValue(csvText);
    onParsedData(data);
  };

  return (
    <div className="space-y-3">
      {label && (
        <Label>
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}

      <div className="flex items-center gap-3">
        <span className={`text-sm ${mode === "text" ? "font-medium" : "text-muted-foreground"}`}>
          {textModeLabel ?? t.textMode}
        </span>
        <Switch
          checked={mode === "file"}
          onCheckedChange={handleModeChange}
          aria-label={t.toggleMode}
        />
        <span className={`text-sm ${mode === "file" ? "font-medium" : "text-muted-foreground"}`}>
          {fileModeLabel ?? t.fileMode}
        </span>
      </div>

      <CsvFileParser
        id={id ? `${id}-file` : undefined}
        onParsed={handleFileParsed}
        errorMessage={fileErrorMessage}
        helpText={fileHelpText}
        buttonText={uploadButtonText ?? t.uploadCsv}
        disabled={mode === "text"}
      />

      <CsvTextParser
        id={id}
        value={textValue}
        onChange={setTextValue}
        onValidationChange={handleTextValidation}
        placeholder={placeholder}
        rows={rows}
        errorMessage={textErrorMessage}
        helpText={textHelpText}
        disabled={mode === "file"}
      />
    </div>
  );
}

export { CsvDataParser };
